import React, { useMemo, useState } from "react";
import { Button, Card, Col, Input, Row, Select, Space, Table, Tag, Typography } from "antd";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const packingSheetData = [
  { id: "MPS-001", orderId: "ORD-56843", customer: "Apex Retail", date: "2026-09-24", assignedTo: "Ravi", material: "Bubble Wrap + Carton Box", boxes: 12, status: "Ready to Pack" },
  { id: "MPS-002", orderId: "ORD-56877", customer: "Urban Cart", date: "2026-09-24", assignedTo: "Sonia", material: "Sealing Tape + Poly Bag", boxes: 8, status: "In Progress" },
  { id: "MPS-003", orderId: "ORD-56910", customer: "North Hub", date: "2026-09-25", assignedTo: "Ajay", material: "Pallet Wrap + Labels", boxes: 15, status: "Pending" },
  { id: "MPS-004", orderId: "ORD-56921", customer: "Luma Mart", date: "2026-09-25", assignedTo: "Nisha", material: "Void Fill + Carton Box", boxes: 10, status: "Ready to Pack" },
  { id: "MPS-005", orderId: "ORD-56938", customer: "Prime Supply", date: "2026-09-26", assignedTo: "Karan", material: "Strapping Band + Tape", boxes: 7, status: "Hold" },
  { id: "MPS-006", orderId: "ORD-56955", customer: "Blue Route", date: "2026-09-26", assignedTo: "Meera", material: "Bubble Wrap + Labels", boxes: 18, status: "In Progress" },
];

const statusColors = {
  "Ready to Pack": "green",
  "In Progress": "blue",
  "Pending": "orange",
  Hold: "red",
};

const columns = [
  { title: "Sheet ID", dataIndex: "id", key: "id" },
  { title: "Order ID", dataIndex: "orderId", key: "orderId" },
  { title: "Customer", dataIndex: "customer", key: "customer" },
  { title: "Packing Date", dataIndex: "date", key: "date" },
  { title: "Assigned To", dataIndex: "assignedTo", key: "assignedTo" },
  { title: "Packing Material", dataIndex: "material", key: "material" },
  { title: "Boxes", dataIndex: "boxes", key: "boxes" },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => <Tag color={statusColors[status] || "default"}>{status}</Tag>,
  },
];

export default function MasterPackingSheetPage({ darkTheme = false, isMobile = false }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const statuses = ["All", ...new Set(packingSheetData.map((item) => item.status))];

  const filteredData = useMemo(() => {
    return packingSheetData.filter((item) => {
      const matchesSearch = item.orderId.toLowerCase().includes(search.toLowerCase()) || item.customer.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "All" || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [search, statusFilter]);

  const summary = {
    totalSheets: packingSheetData.length,
    readyToPack: packingSheetData.filter((item) => item.status === "Ready to Pack").length,
    inProgress: packingSheetData.filter((item) => item.status === "In Progress").length,
    pending: packingSheetData.filter((item) => item.status === "Pending").length,
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24, background: darkTheme ? "#141414" : "#f5f5f5", minHeight: "100vh" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Text type="secondary">Packing Operations</Text>
            <Title level={3} style={{ margin: 0, color: darkTheme ? "#fff" : "#111827" }}>Master Packing Sheet</Title>
          </div>
          <Button type="primary">Create Sheet</Button>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: darkTheme ? "#1f1f1f" : "#fff", border: "none" }}>
              <Text type="secondary">Total Sheets</Text>
              <Title level={3} style={{ marginTop: 8, marginBottom: 0, color: darkTheme ? "#fff" : "#111827" }}>{summary.totalSheets}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: darkTheme ? "#1f1f1f" : "#fff", border: "none" }}>
              <Text type="secondary">Ready to Pack</Text>
              <Title level={3} style={{ marginTop: 8, marginBottom: 0, color: darkTheme ? "#fff" : "#111827" }}>{summary.readyToPack}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: darkTheme ? "#1f1f1f" : "#fff", border: "none" }}>
              <Text type="secondary">In Progress</Text>
              <Title level={3} style={{ marginTop: 8, marginBottom: 0, color: darkTheme ? "#fff" : "#111827" }}>{summary.inProgress}</Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: darkTheme ? "#1f1f1f" : "#fff", border: "none" }}>
              <Text type="secondary">Pending</Text>
              <Title level={3} style={{ marginTop: 8, marginBottom: 0, color: darkTheme ? "#fff" : "#111827" }}>{summary.pending}</Title>
            </Card>
          </Col>
        </Row>

        <Card style={{ background: darkTheme ? "#1f1f1f" : "#fff", border: "none" }}>
          <Space direction={isMobile ? "vertical" : "horizontal"} style={{ width: "100%", marginBottom: 16 }}>
            <Search
              placeholder="Search order or customer"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: isMobile ? "100%" : 260 }}
            />
            <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 180 }}>
              {statuses.map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>

          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 7 }}
            bordered
            scroll={{ x: true }}
            size="middle"
          />
        </Card>
      </Space>
    </div>
  );
}
